import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Coins, Gift, TrendingUp, Users, AlertTriangle, ArrowUpRight, ArrowDownRight, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../lib/api';

const AdminCoins = () => {
  const [stats, setStats] = useState(null);
  const [recentGifts, setRecentGifts] = useState([]);
  const [topGifters, setTopGifters] = useState([]);
  const [topReceivers, setTopReceivers] = useState([]);
  const [suspiciousActivity, setSuspiciousActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, giftsRes, topGiftersRes, topReceiversRes, suspiciousRes] = await Promise.all([
        api.get('/admin/coins/stats'),
        api.get('/admin/coins/recent-gifts?limit=20'),
        api.get('/admin/coins/top-gifters?limit=10'),
        api.get('/admin/coins/top-receivers?limit=10'),
        api.get('/admin/coins/suspicious-activity'),
      ]);

      if (statsRes.data) setStats(statsRes.data);
      if (giftsRes.data) setRecentGifts(giftsRes.data.gifts || []);
      if (topGiftersRes.data) setTopGifters(topGiftersRes.data.users || []);
      if (topReceiversRes.data) setTopReceivers(topReceiversRes.data.users || []);
      if (suspiciousRes.data) setSuspiciousActivity(suspiciousRes.data.alerts || []);
    } catch (error) {
      console.error('Failed to fetch coin stats:', error);
      toast.error('Failed to load coin statistics');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-coins">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Coins className="h-4 w-4" />
              Total Coins in Circulation
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.total_coins_in_circulation?.toLocaleString() || 0}</div>
            <p className="text-xs text-muted-foreground">Across {stats?.users_with_coins || 0} users</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Gift className="h-4 w-4" />
              Gifts Sent Today
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.gifts_today || 0}</div>
            <p className="text-xs text-muted-foreground">
              {stats?.gifts_this_week || 0} this week
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              Coins Spent Today
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.coins_spent_today?.toLocaleString() || 0}</div>
            <p className="text-xs text-muted-foreground">
              {stats?.coins_spent_this_week?.toLocaleString() || 0} this week
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Users className="h-4 w-4" />
              Active Gifters
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.active_gifters_today || 0}</div>
            <p className="text-xs text-muted-foreground">Sent gifts today</p>
          </CardContent>
        </Card>
      </div>

      {/* Suspicious Activity Alert */}
      {suspiciousActivity.length > 0 && (
        <Card className="border-red-200 bg-red-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="h-5 w-5" />
              Suspicious Activity Detected
            </CardTitle>
            <CardDescription className="text-red-600">
              Review these flagged transactions
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {suspiciousActivity.map((alert, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-white rounded-lg border border-red-200">
                  <div>
                    <span className="font-medium text-red-700">{alert.type}</span>
                    <p className="text-sm text-red-600">{alert.description}</p>
                  </div>
                  <Badge variant="destructive">{alert.severity}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Gifters */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ArrowUpRight className="h-5 w-5 text-green-500" />
              Top Gifters
            </CardTitle>
            <CardDescription>Users who sent the most gifts</CardDescription>
          </CardHeader>
          <CardContent>
            {topGifters.length === 0 ? (
              <p className="text-muted-foreground text-center py-4">No gifting activity yet</p>
            ) : (
              <div className="space-y-3">
                {topGifters.map((user, idx) => (
                  <div key={user.user_id} className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50">
                    <div className="flex items-center gap-3">
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                        idx === 0 ? 'bg-yellow-100 text-yellow-700' :
                        idx === 1 ? 'bg-gray-100 text-gray-700' :
                        idx === 2 ? 'bg-orange-100 text-orange-700' :
                        'bg-muted text-muted-foreground'
                      }`}>
                        {idx + 1}
                      </span>
                      <div>
                        <p className="font-medium">{user.name || 'Unknown'}</p>
                        <p className="text-xs text-muted-foreground">{user.email}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold">{user.gifts_sent}</p>
                      <p className="text-xs text-muted-foreground">{user.coins_spent} coins</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top Receivers */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ArrowDownRight className="h-5 w-5 text-blue-500" />
              Top Gift Receivers
            </CardTitle>
            <CardDescription>Users who received the most gifts</CardDescription>
          </CardHeader>
          <CardContent>
            {topReceivers.length === 0 ? (
              <p className="text-muted-foreground text-center py-4">No gifting activity yet</p>
            ) : (
              <div className="space-y-3">
                {topReceivers.map((user, idx) => (
                  <div key={user.user_id} className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50">
                    <div className="flex items-center gap-3">
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                        idx === 0 ? 'bg-yellow-100 text-yellow-700' :
                        idx === 1 ? 'bg-gray-100 text-gray-700' :
                        idx === 2 ? 'bg-orange-100 text-orange-700' :
                        'bg-muted text-muted-foreground'
                      }`}>
                        {idx + 1}
                      </span>
                      <div>
                        <p className="font-medium">{user.name || 'Unknown'}</p>
                        <p className="text-xs text-muted-foreground">{user.email}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold">{user.gifts_received}</p>
                      <p className="text-xs text-muted-foreground">gifts received</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Gifts */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Recent Gift Activity
          </CardTitle>
          <CardDescription>Latest gifts sent between users</CardDescription>
        </CardHeader>
        <CardContent>
          {recentGifts.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">No gifts sent yet</p>
          ) : (
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {recentGifts.map((gift) => (
                <div key={gift.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{gift.gift_emoji}</span>
                    <div>
                      <p className="text-sm">
                        <span className="font-medium">{gift.sender_name || 'Unknown'}</span>
                        <span className="text-muted-foreground"> → </span>
                        <span className="font-medium">{gift.recipient_name || 'Unknown'}</span>
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {gift.gift_name} • {gift.cost} coins
                        {gift.message && ` • "${gift.message}"`}
                      </p>
                    </div>
                  </div>
                  <span className="text-xs text-muted-foreground">{formatDate(gift.created_at)}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminCoins;
